import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Power, PowerOff, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { downloadCsv, toCsv } from "@/lib/csv";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import { useAuth } from "@/context/AuthContext";
import {
  Field,
  GhostButton,
  OutlineButton,
  PrimaryButton,
  SearchBox,
  StatusBadge,
  inputCls,
  selectCls,
} from "@/components/masters/UI";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const empty = {
  employee_id: "",
  name: "",
  location_id: "",
  shift_id: "",
  department_id: "",
  job_role_id: "",
  salary: 0,
  status: "active",
};

const EmployeesPage = () => {
  const { user, isLocationAdmin, selectedLocationId } = useAuth();
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [allJobRoles, setAllJobRoles] = useState([]);
  const [q, setQ] = useState("");
  const [locFilter, setLocFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const loadCore = useCallback(async () => {
    const promises = [
      api.get("/departments", { params: { only_active: true } }),
      api.get("/shifts", { params: { only_active: true } }),
      api.get("/job-roles"),
    ];
    if (!isLocationAdmin) {
      promises.push(api.get("/locations", { params: { only_active: true } }));
    }
    const [depsRes, shiftsRes, rolesRes, locsRes] = await Promise.all(promises);
    if (!isLocationAdmin && locsRes) {
      setLocations(locsRes.data);
    } else if (user?.location_id) {
      setLocations([{ id: user.location_id, name: user.location_name || "Assigned Location" }]);
    }
    setDepartments(depsRes.data);
    setShifts(shiftsRes.data);
    setAllJobRoles(rolesRes.data);
    setJobRoles(rolesRes.data.filter((r) => r.status === "active"));
  }, [isLocationAdmin, user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (locFilter && !isLocationAdmin) params.location_id = locFilter;
      const [empsRes] = await Promise.all([
        api.get("/employees", { params }),
        loadCore(),
      ]);
      setRows(empsRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, locFilter, loadCore, selectedLocationId, isLocationAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const nameOf = (list, id) => list.find((x) => x.id === id)?.name || "—";

  const scopedShifts = useMemo(
    () => shifts.filter((s) => !form.location_id || s.location_id === form.location_id),
    [shifts, form.location_id]
  );
  const scopedDepartments = useMemo(
    () => departments.filter((d) => !form.location_id || d.location_id === form.location_id),
    [departments, form.location_id]
  );
  const scopedJobRoles = useMemo(() => {
    const locId = form.location_id;
    const active = jobRoles.filter((jr) => !locId || jr.location_id === locId);
    // If editing an employee that already has a deactivated Job Role, keep it in the dropdown
    if (editing && editing.job_role_id) {
      const current = allJobRoles.find((jr) => jr.id === editing.job_role_id);
      if (
        current &&
        (!locId || current.location_id === locId) &&
        !active.some((r) => r.id === current.id)
      ) {
        return [current, ...active];
      }
    }
    return active;
  }, [jobRoles, allJobRoles, form.location_id, editing]);

  const startNew = () => {
    setEditing(null);
    const firstLoc = isLocationAdmin
      ? (user?.location_id || "")
      : (selectedLocationId || locations[0]?.id || "");
    setForm({
      ...empty,
      location_id: firstLoc,
      shift_id: "",
      department_id: "",
      job_role_id: "",
    });
    setErr(null);
    setOpen(true);
  };

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      employee_id: row.employee_id,
      name: row.name,
      location_id: row.location_id,
      shift_id: row.shift_id,
      department_id: row.department_id,
      job_role_id: row.job_role_id || "",
      salary: row.salary,
      status: row.status,
    });
    setErr(null);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.employee_id.trim() || !form.name.trim())
      return setErr("Employee ID and Name are required.");
    if (!isLocationAdmin && !form.location_id) return setErr("Location is required.");
    if (!form.job_role_id) return setErr("Job Role is required.");
    if (!form.shift_id) return setErr("Shift is required.");
    if (!form.department_id) return setErr("Department is required.");
    const sal = Number(form.salary);
    if (!Number.isFinite(sal) || sal < 0) return setErr("Salary must be a non-negative number.");

    setSaving(true);
    try {
      const body = { ...form, salary: sal };
      if (isLocationAdmin) {
        delete body.location_id;
      }
      if (editing) {
        await api.put(`/employees/${editing.id}`, body);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", body);
        toast.success("Employee created");
      }
      setOpen(false);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    const next = row.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/employees/${row.id}/status`, { status: next });
      toast.success(next === "active" ? "Activated" : "Inactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const canCreate = (isLocationAdmin || locations.length) && departments.length && shifts.length && jobRoles.length;

  // Changing an employee's Location clears the Department, Shift and Job Role selections.
  const onLocationChange = (locId) => {
    setForm((f) => ({
      ...f,
      location_id: locId,
      shift_id: "",
      department_id: "",
      job_role_id: "",
    }));
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    const csv = toCsv(rows, [
      { header: "Employee ID", get: (r) => r.employee_id },
      { header: "Name", get: (r) => r.name },
      { header: "Location", get: (r) => nameOf(locations, r.location_id) },
      {
        header: "Job Role",
        get: (r) => r.job_role_name || (r.job_role_id ? nameOf(allJobRoles, r.job_role_id) : "") || "",
      },
      { header: "Department", get: (r) => nameOf(departments, r.department_id) },
      { header: "Shift", get: (r) => nameOf(shifts, r.shift_id) },
      { header: "Status", get: (r) => r.status },
    ]);
    downloadCsv(`employees-${new Date().toISOString().slice(0, 10)}`, csv);
    toast.success(`Exported ${rows.length} employees`);
  };

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="employees-page">
      <AppHeader />
      <PageShell
        eyebrow="Masters · Employees"
        title="Employees"
        subtitle="QR-ready roster. Employee ID is unique and will be encoded in the QR code later."
        primaryAction={
          <div className="flex items-center gap-2">
            <OutlineButton
              testId="emp-export-csv"
              onClick={exportCsv}
              disabled={rows.length === 0}
            >
              <Download className="mr-1 inline h-4 w-4" /> Export CSV
            </OutlineButton>
            <PrimaryButton testId="emp-add" onClick={startNew} disabled={!canCreate}>
              <Plus className="mr-1 inline h-4 w-4" /> Add Employee
            </PrimaryButton>
          </div>
        }
        filters={
          <>
            <SearchBox
              value={q}
              onChange={setQ}
              placeholder="Search Employee ID or Name…"
              testId="emp-search"
            />
            {!isLocationAdmin && (
              <select
                className={selectCls + " w-56"}
                data-testid="emp-filter-location"
                value={locFilter}
                onChange={(e) => setLocFilter(e.target.value)}
                aria-label="Filter by Location"
              >
                <option value="">All locations</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            )}
          </>
        }
        testId="employees-shell"
      >
        {!canCreate && (
          <div
            className="mb-4 rounded-2xl border p-4 text-sm"
            data-testid="emp-prereq"
            style={{
              backgroundColor: "rgba(242, 153, 74, 0.10)",
              borderColor: "rgba(242, 153, 74, 0.35)",
              color: "#7a3f0a",
            }}
          >
            Add active <b>Locations</b>, <b>Job Roles</b>, <b>Departments</b> and <b>Shifts</b> first,
            then come back here.
          </div>
        )}

        <div
          className="overflow-hidden rounded-2xl border bg-white shadow-card"
          style={{ borderColor: "rgba(52,73,94,0.08)" }}
        >
          <div className="hidden grid-cols-[120px_1.4fr_1fr_1.1fr_1fr_1fr_90px_100px_130px] gap-3 bg-[color:var(--vision-ice)] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60 md:grid">
            <div>Employee ID</div>
            <div>Name</div>
            <div>Location</div>
            <div>Job Role</div>
            <div>Department</div>
            <div>Shift</div>
            <div>Salary</div>
            <div>Status</div>
            <div className="text-right">Actions</div>
          </div>

          {loading && (
            <div
              className="px-5 py-10 text-center text-sm text-vision-slate/60"
              data-testid="emp-loading"
            >
              Loading…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div
              className="px-5 py-14 text-center text-sm text-vision-slate/60"
              data-testid="emp-empty"
            >
              No employees found.
            </div>
          )}

          {!loading &&
            rows.map((row) => (
              <div
                key={row.id}
                data-testid={`emp-row-${row.id}`}
                className="grid grid-cols-1 items-center gap-3 border-t px-5 py-4 md:grid-cols-[120px_1.4fr_1fr_1.1fr_1fr_1fr_90px_100px_130px] md:gap-3"
                style={{ borderColor: "rgba(52,73,94,0.08)" }}
              >
                <div className="font-display text-sm font-bold text-vision-ink">
                  {row.employee_id}
                </div>
                <div className="text-sm font-semibold text-vision-ink">{row.name}</div>
                <div className="text-sm text-vision-slate">{nameOf(locations, row.location_id)}</div>
                <div className="text-sm font-medium text-vision-ink" data-testid={`emp-job-role-${row.id}`}>
                  {row.job_role_name || (row.job_role_id ? nameOf(allJobRoles, row.job_role_id) : "") || "—"}
                </div>
                <div className="text-sm text-vision-slate">{nameOf(departments, row.department_id)}</div>
                <div className="text-sm text-vision-slate">{nameOf(shifts, row.shift_id)}</div>
                <div className="font-display text-sm font-bold text-vision-ink numeric-tabular">
                  {Number(row.salary).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
                <div>
                  <StatusBadge active={row.status === "active"} />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <GhostButton testId={`emp-edit-${row.id}`} onClick={() => startEdit(row)}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </GhostButton>
                  <GhostButton testId={`emp-toggle-${row.id}`} onClick={() => toggleStatus(row)}>
                    {row.status === "active" ? (
                      <>
                        <PowerOff className="h-3.5 w-3.5" /> Inactivate
                      </>
                    ) : (
                      <>
                        <Power className="h-3.5 w-3.5" /> Activate
                      </>
                    )}
                  </GhostButton>
                </div>
              </div>
            ))}
        </div>
      </PageShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-2xl" data-testid="emp-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-vision-ink">
              {editing ? "Edit Employee" : "Add Employee"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={submit} className="mt-2 grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Employee ID" hint="Unique — used as the QR value.">
                <input
                  className={inputCls}
                  data-testid="emp-form-employee-id"
                  value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  placeholder="e.g. VSN-1051"
                />
              </Field>
              <Field label="Name">
                <input
                  className={inputCls}
                  data-testid="emp-form-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Employee's full name"
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Location">
                {isLocationAdmin ? (
                  <input
                    className={inputCls}
                    disabled
                    value={user?.location_name || "Assigned Location"}
                  />
                ) : (
                  <select
                    className={selectCls}
                    data-testid="emp-form-location"
                    value={form.location_id}
                    onChange={(e) => onLocationChange(e.target.value)}
                  >
                    <option value="">Select a location…</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>

              <Field label="Job Role" hint="Required. Filtered by Location.">
                <select
                  className={selectCls}
                  data-testid="emp-form-job-role"
                  value={form.job_role_id}
                  onChange={(e) => setForm({ ...form, job_role_id: e.target.value })}
                  disabled={!form.location_id}
                >
                  <option value="">Select a job role…</option>
                  {scopedJobRoles.map((jr) => (
                    <option key={jr.id} value={jr.id}>
                      {jr.name} ({jr.code})
                      {jr.status !== "active" ? " · (Inactive)" : ""}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Department" hint="Filtered by selected Location.">
                <select
                  className={selectCls}
                  data-testid="emp-form-department"
                  value={form.department_id}
                  onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                  disabled={!form.location_id}
                >
                  <option value="">Select…</option>
                  {scopedDepartments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Shift" hint="Filtered by selected Location.">
                <select
                  className={selectCls}
                  data-testid="emp-form-shift"
                  value={form.shift_id}
                  onChange={(e) => setForm({ ...form, shift_id: e.target.value })}
                  disabled={!form.location_id}
                >
                  <option value="">Select…</option>
                  {scopedShifts.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.start_time}-{s.end_time}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Salary">
                <input
                  className={inputCls}
                  type="number"
                  min={0}
                  step="0.01"
                  data-testid="emp-form-salary"
                  value={form.salary}
                  onChange={(e) => setForm({ ...form, salary: e.target.value })}
                />
              </Field>
              <Field label="Status">
                <select
                  className={selectCls}
                  data-testid="emp-form-status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </Field>
            </div>

            {err && (
              <div
                data-testid="emp-form-error"
                className="rounded-xl border px-4 py-3 text-sm"
                style={{
                  backgroundColor: "rgba(231, 76, 60, 0.08)",
                  borderColor: "rgba(231, 76, 60, 0.3)",
                  color: "var(--vision-error)",
                }}
              >
                {err}
              </div>
            )}

            <DialogFooter className="mt-2 gap-2 sm:justify-end">
              <OutlineButton
                type="button"
                testId="emp-form-cancel"
                onClick={() => setOpen(false)}
              >
                Cancel
              </OutlineButton>
              <PrimaryButton type="submit" testId="emp-form-submit" disabled={saving}>
                {saving ? "Saving…" : editing ? "Save changes" : "Create"}
              </PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
